/**
 * URL param keys and AG-Grid filter/sort mapping for findings.
 * Keeps URL shape compatible with existing FindingsGrid (useSearchParamsState).
 */
export type FindingsGridState = {
    sortModel: Array<{ colId: string; sort: 'asc' | 'desc' }>;
    filterModel: Record<string, any>;
};

export type FilterFieldMapEntry = {
    modelKey: string;
    valueKey: string | string[];
    filterType: string;
    urlKey: string;
};

/** Maps AG-Grid colId/filter model keys to URL param keys (same as FindingsGrid). */
export const findingsFilterFieldMap: Record<string, FilterFieldMapEntry> = {
    userId: {
        modelKey: 'user_id',
        valueKey: 'values',
        filterType: 'set',
        urlKey: 'user_id',
    },
    severity: {
        modelKey: 'severity',
        valueKey: 'values',
        filterType: 'set',
        urlKey: 'severity',
    },
    findingStatus: {
        modelKey: 'finding_status',
        valueKey: 'values',
        filterType: 'set',
        urlKey: 'finding_status',
    },
    findingCategory: {
        modelKey: 'finding_category',
        valueKey: 'values',
        filterType: 'set',
        urlKey: 'finding_category',
    },
    findingKind: {
        modelKey: 'finding_kind',
        valueKey: 'values',
        filterType: 'set',
        urlKey: 'finding_kind',
    },
    findingTags: {
        modelKey: 'finding_tags',
        valueKey: 'values',
        filterType: 'tagMultiSelect',
        urlKey: 'finding_tags',
    },
    providerName: {
        modelKey: 'provider_name',
        valueKey: 'values',
        filterType: 'set',
        urlKey: 'provider_name',
    },
    userName: {
        modelKey: 'user_name',
        valueKey: 'filterValues',
        filterType: 'userNameColumn',
        urlKey: 'user_name',
    },
    deviceDisplayValue: {
        modelKey: 'device_display_value',
        valueKey: 'values',
        filterType: 'text',
        urlKey: 'device_display_value',
    },
    userDepartment: {
        modelKey: 'user_department',
        valueKey: 'values',
        filterType: 'departmentMultiSelect',
        urlKey: 'user_department',
    },
    findingTitle: {
        modelKey: 'finding_title',
        valueKey: 'values',
        filterType: 'set',
        urlKey: 'finding_title',
    },
};

export const GRID_CONFIG = {
    defaultHeight: '37.5rem',
    minHeight: '25rem',
    paginationPageSize: 100,
} as const;

/** Default column visibility and order for reset / initial load when no saved state. */
export const DEFAULT_COLUMN_STATE = [
    { colId: 'severity', hide: false, width: 135 },
    { colId: 'finding_status', hide: false, width: 130 },
    { colId: 'finding_category', hide: false, width: 165 },
    { colId: 'provider_name', hide: false, width: 150 },
    { colId: 'finding_title', hide: false, width: 230 },
    { colId: 'finding_kind', hide: false, width: 230 },
    { colId: 'finding_tags', hide: true, width: 160 },
    { colId: 'user_name', hide: false, width: 160 },
    { colId: 'user_department', hide: false, width: 160 },
    { colId: 'device_display_value', hide: false, width: 210 },
    { colId: 'discoveredAt', hide: false, width: 200 },
    { colId: 'description', hide: false, width: 500 },
    { colId: 'triggerable_agents_count', hide: false, width: 160 },
];

/** Default column state when triggerable agents count is enabled (APD-1629). */
export const DEFAULT_COLUMN_STATE_TRIGGERABLE_AGENTS = DEFAULT_COLUMN_STATE.map((column) =>
    column.colId === 'number_of_engagements' ? { ...column, colId: 'triggerable_agents_count', width: 160 } : column,
).filter((column) => column.colId !== 'number_of_engagements');

export const getMinWidthForColumn = (column: string) => {
    return (
        DEFAULT_COLUMN_STATE.find((c) => c.colId === column)?.width ??
        DEFAULT_COLUMN_STATE_TRIGGERABLE_AGENTS.find((c) => c.colId === column)?.width
    );
};

export const COLUMN_STATE_STORAGE_KEYS = {
    server: 'agFindingsGridColumnStateServer',
    queryStringParams: 'agFindingsGrid_queryStringParams',
} as const;

export interface AgFindingsGridProps {
    /** Base filter criteria (e.g. from page context or findingsModel). Merged with URL/filter state. */
    filter?: Record<string, any>;
    /**
     * Default finding filters (e.g. modal context) merged into requests and grid filter state when
     * those keys are absent from the URL. URL / grid changes override for keys that are set.
     * Supports set-filter keys (severity, finding_kind, …) and `user_associated=any`.
     */
    defaultFilter?: Record<string, string | string[]>;
    /** Optional filter by finding category (e.g. from AnalyticsDashboardPage). */
    findingCategory?: string | string[];
    /** Optional filter by finding kind (e.g. from AnalyticsDashboardPage dropdown). */
    findingKind?: string | string[];
    tableBodyHeight?: string;
    /** Sync filter/sort to URL (default true). */
    enableUpdatingURLWithFiltersAndSorting?: boolean;
    /** Show sub-header with actions (default false). */
    showSubHeader?: boolean;
    /** Disable column filters (default false). */
    disableFilters?: boolean;
    /** Allow row selection for actions (default follows auth). */
    selectableRows?: boolean;
    /** Callback when selection changes. */
    onSelectionChange?: (selectedRows: any[]) => void;
    /** Optional title above grid. */
    title?: string;
    /** Reset URL state when this becomes true. */
    reset?: boolean;
    /** When false, hides the results count and Actions button (e.g. for embedded use on Analytics). */
    showHeaderActions?: boolean;
    /** When true, grid root uses full width of container (e.g. inside cards). */
    fullWidth?: boolean;
    /** When set, these URL param keys are preserved when the grid updates filter/sort in the URL. */
    preserveUrlParamKeys?: string[];
    /** When set, shows only columns with the given colIds; all other columns are hidden. */
    showOnlyColumns?: string[];
    /** When set, columns with the given colIds are hidden from the default set of columns. */
    omitColumns?: string[];
    /** Optional empty-state message shown when no rows are returned. */
    emptyStateDescription?: string;
    /**
     * When true, this grid does not read/write shared column layout in localStorage, hides the
     * Columns side panel, and disables resize/reorder so the view stays fixed (e.g. user detail).
     */
    isolateColumnLayout?: boolean;
    /**
     * When false, hides the header Actions menu (Export CSV / Reset columns).
     * When omitted, follows `showHeaderActions` (so embedded grids with `showHeaderActions={false}` stay chromeless).
     */
    showActionsMenu?: boolean;
    /**
     * When false, hides the columns menu (e.g. user detail).
     * When omitted, follows `showColumnsMenu` (so embedded grids with `showColumnsMenu={false}` stay chromeless).
     */
    showSidebar?: boolean;
    /**
     * AG-Grid filter model applied on grid ready when URL sync is disabled.
     * Useful for embedded grids (e.g. user detail) that need a default filter
     * (e.g. show only OPEN findings) without persisting it in the URL.
     */
    defaultFilterModel?: Record<string, any>;
    /**
     * When true, uses AG Grid pagination controls instead of infinite scroll. Defaults to false so
     * existing pages keep the current behavior until you opt in.
     */
    usePagination?: boolean;
}
